import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ModalAgendaComponent } from '@base/components/modal-agenda/modal-agenda.component';
import { SearchReason } from '@base/models/others/match-reason';
import { AgendaSession } from '@base/models/responses/agenda-session.res';
import { InformaMfeMatchContext } from '@base/models/others/informa-mfe-event';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { NgbCarouselModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-agenda-detail',
  imports: [NgbCarouselModule],
  templateUrl: './agenda-detail.component.html',
  styleUrl: './agenda-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgendaDetailComponent implements AfterViewInit {
  @ViewChild('sessionDetailContainer', { read: ViewContainerRef }) sessionDetailContainer!: ViewContainerRef;
  @Input() sessionId: string | null = null;
  @Input() modalId = '';
  @Input() descriptionMatchQuery: string | null = null;
  @Input() isShowFavorite = true;
  @Input() isShowPrevNextControl = true;
  @Input() searchReasons: SearchReason[] | null = null;
  @Input() isCloseAll = true;
  @Input() matchContext: InformaMfeMatchContext | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);

  sessionDetailInstance: ComponentRef<unknown> | undefined;
  agenda: AgendaSession | null = null;

  ngAfterViewInit(): void {
    (async () => this.loadSessionDetailComponent())();
  }

  /**
   * Dynamically loads the session detail component into the view.
   */
  async loadSessionDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.sessionDetailContainer,
        WhiteLabel.COMPONENTS.ModalAgenda // Replace with actual path
      );
      if (this.destroyRef.destroyed) return;
      if (component) {
        this.sessionDetailInstance = component.componentRef || undefined;
        this.sessionDetailInstance?.setInput('isShowPrevNextControl', this.isShowPrevNextControl);
        this.sessionDetailInstance?.setInput('isCloseAll', this.isCloseAll);

        this.renderSpeakerDetail();
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Renders speaker details in the modal component.
   */
  renderSpeakerDetail(): void {
    const component = this.sessionDetailInstance?.instance as ModalAgendaComponent; // Replace with actual component type
    if (component) {
      this.sessionDetailInstance?.setInput('sessionId', this.sessionId);
      this.sessionDetailInstance?.setInput('modalId', this.modalId);
      this.sessionDetailInstance?.setInput('isShowFavorite', this.isShowFavorite);
      this.sessionDetailInstance?.setInput('searchReasons', this.searchReasons);
      this.sessionDetailInstance?.setInput('descriptionMatchQuery', this.descriptionMatchQuery);
      if (this.matchContext) {
        this.sessionDetailInstance?.setInput('matchContext', this.matchContext);
      }
    }
  }
}
